import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedPrivacyPolicy } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-privacy-policies.css";

const DeletedPrivacyPolicies: React.FC = () => {
  const [policies, setPolicies] = useState<DeletedPrivacyPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPolicies, setSelectedPolicies] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedPolicy, setSelectedPolicy] = useState<DeletedPrivacyPolicy | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedPrivacyPolicies(page, limit);
      setPolicies(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
      setPolicies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const handleRestore = async (policyId: number) => {
    try {
      await trashService.restorePrivacyPolicy(policyId);
      setPolicies(policies.filter(policy => policy.id !== policyId));
      setSelectedPolicies(prev => prev.filter(id => id !== policyId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục chính sách bảo mật');
    }
  };

  const handlePermanentDelete = async (policyId: number) => {
    try {
      await trashService.permanentDeletePrivacyPolicy(policyId);
      setPolicies(policies.filter(policy => policy.id !== policyId));
      setSelectedPolicies(prev => prev.filter(id => id !== policyId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn chính sách bảo mật');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestorePrivacyPolicies(selectedPolicies);
      setPolicies(policies.filter(policy => !selectedPolicies.includes(policy.id)));
      setSelectedPolicies([]);
      setTotal(prev => prev - selectedPolicies.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các chính sách bảo mật');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeletePrivacyPolicies(selectedPolicies);
      setPolicies(policies.filter(policy => !selectedPolicies.includes(policy.id)));
      setSelectedPolicies([]);
      setTotal(prev => prev - selectedPolicies.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các chính sách bảo mật');
    }
  };

  const toggleSelectPolicy = (policyId: number) => {
    setSelectedPolicies(prev => 
      prev.includes(policyId) 
        ? prev.filter(id => id !== policyId)
        : [...prev, policyId]
    );
  };

  const selectAll = () => {
    setSelectedPolicies((policies || []).map(policy => policy.id));
  };

  const deselectAll = () => {
    setSelectedPolicies([]);
  };

  const handleViewDetails = (policy: DeletedPrivacyPolicy) => {
    setSelectedPolicy(policy);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPolicy(null);
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách chính sách bảo mật đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadPolicies} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-privacy-policies">
      <div className="deleted-privacy-policies__header">
        <div className="deleted-privacy-policies__info">
          <h3 className="deleted-privacy-policies__title">Chính sách bảo mật đã xóa ({(policies || []).length})</h3>
          <p className="deleted-privacy-policies__description">Danh sách các chính sách bảo mật đã bị xóa và có thể khôi phục</p>
        </div>
        
        {(policies || []).length > 0 && (
          <div className="deleted-privacy-policies__bulk-actions">
            <button 
              className="deleted-privacy-policies__bulk-btn deleted-privacy-policies__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedPolicies.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedPolicies.length})
            </button>
            <button 
              className="deleted-privacy-policies__bulk-btn deleted-privacy-policies__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedPolicies.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedPolicies.length})
            </button>
            <button 
              className="deleted-privacy-policies__bulk-btn deleted-privacy-policies__bulk-btn--select"
              onClick={selectedPolicies.length === (policies || []).length ? deselectAll : selectAll}
            >
              {selectedPolicies.length === (policies || []).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {(policies || []).length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có chính sách bảo mật nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả chính sách bảo mật đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-privacy-policies__list">
          {(policies || []).map((policy) => (
            <div key={policy.id} className="deleted-privacy-policies__item">
              <div className="deleted-privacy-policies__checkbox">
                <input
                  type="checkbox"
                  checked={selectedPolicies.includes(policy.id)}
                  onChange={() => toggleSelectPolicy(policy.id)}
                />
              </div>
              
              <div className="deleted-privacy-policies__content">
                <div className="deleted-privacy-policies__item-header">
                  <h4 className="deleted-privacy-policies__item-title">{policy.title}</h4>
                  <span className="deleted-privacy-policies__item-status deleted-privacy-policies__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-privacy-policies__meta">
                  <div className="deleted-privacy-policies__meta-item">
                    <User size={14} />
                    <span>Xóa bởi: {policy.deletedBy ? `User ID: ${policy.deletedBy}` : 'Không xác định'}</span>
                  </div>
                  <div className="deleted-privacy-policies__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(policy.deletedAt)}</span>
                  </div>
                  <div className="deleted-privacy-policies__meta-item">
                    <span>Phiên bản: {policy.version}</span>
                  </div>
                  <div className="deleted-privacy-policies__meta-item">
                    <span>Trạng thái: {policy.isActive ? "Hoạt động" : "Tạm dừng"}</span>
                  </div>
                  {policy.filePath && (
                    <div className="deleted-privacy-policies__meta-item">
                      <span>📄 Có file đính kèm</span>
                    </div>
                  )}
                </div>
                
                <div className="deleted-privacy-policies__preview">
                  {policy.content ? policy.content.substring(0, 200) + "..." : "Không có nội dung text"}
                </div>
              </div>
              
              <div className="deleted-privacy-policies__actions">
                <button 
                  className="deleted-privacy-policies__action-btn deleted-privacy-policies__action-btn--preview"
                  onClick={() => handleViewDetails(policy)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-privacy-policies__action-btn deleted-privacy-policies__action-btn--restore"
                  onClick={() => handleRestore(policy.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-privacy-policies__action-btn deleted-privacy-policies__action-btn--delete"
                  onClick={() => handlePermanentDelete(policy.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-privacy-policies__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-privacy-policies__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-privacy-policies__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-privacy-policies__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedPolicy}
        type="privacy-policy"
      />
    </div>
  );
};

export default DeletedPrivacyPolicies;
